// --------------------------------
// Copyright (c) 2023 Eco8 S.r.l.
// --------------------------------
import React, { useState, useEffect } from "react";
import { View, Text, StyleSheet, TouchableOpacity, Switch } from "react-native";
import { CheckBox } from "react-native-elements";
import NumberSelector from "./NumberSelector";
import Picker from "./ComboPicker";
import { FontAwesome } from "@expo/vector-icons";
import {
    insertNewRequest,
    editRequestData,
    fetchHandleMaterials,
} from "../../../../../axios/axiosConfig";
import { useNavigation } from "@react-navigation/native";
import colors from "../../../../../config/colors";
import fonts from "../../../../../config/fonts";

const NewRequestCard = ({
    collapsed,
    getTranslation,
    handleAlert,
    handleClosingModal,
    colors,
    editMode,
    selectedTrip,
    transferId,
    bathtubsToConfirm,
    bathtubsDestination,
    selectedBathtubToConfirm,
    setSelectedBathtubToConfirm,
    handleReload,
}) => {
    console.log("selectedBathtubDestination = ", selectedBathtubToConfirm);
    const navigation = useNavigation();
    const logger = require("../../../../../axios/loggingConfig");

    const initialMaterials = [
        { id: "Liquame", name: getTranslation("Liquame") },
        { id: "Letame", name: getTranslation("Letame") },
        { id: "DigestatoLiquido", name: getTranslation("Digestato Liquido") },
        { id: "DigestatoSecco", name: getTranslation("digestato separato") },
    ];

    const [materials, setMaterials] = useState([]);
    const [selectedMaterial, setSelectedMaterial] = useState(
        editMode ? selectedTrip.contentType : ""
    );
    const [numberOfTrips, setNumberOfTrips] = useState(1);
    const [isUrgent, setIsUrgent] = useState(
        editMode ? selectedTrip.isUrgent : false
    );
    const [toCall, setToCall] = useState(editMode ? selectedTrip.toCall : false);
    const [allChecked, setAllChecked] = useState(true);
    const [highlightedIndex, setHighlightedIndex] = useState(null);

    const [snackbarVisible, setSnackbarVisible] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarColor, setSnackbarColor] = useState(colors.green);

    useEffect(() => {
        const fetchMaterials = async () => {
            try {
                const response = await fetchHandleMaterials();
                if (response.status === 200) {
                    const fetchedIds = response.data;
                    let filteredMaterials = initialMaterials.filter((material) =>
                        fetchedIds.includes(material.id)
                    );

                    if (!bathtubsToConfirm || bathtubsToConfirm.length === 0) {
                        filteredMaterials = filteredMaterials.filter(
                            (material) => material.id !== "DigestatoLiquido"
                        );
                    }
                    /* temporary removed
 
          const digestatoSecco = initialMaterials.find(
            (material) => material.id === "DigestatoSecco"
          );
          if (
            digestatoSecco &&
            !filteredMaterials.find(
              (material) => material.id === "DigestatoSecco"
            )
          ) {
            filteredMaterials.push(digestatoSecco);
          }*/
                    setMaterials(filteredMaterials);
                    if (!editMode && filteredMaterials.length > 0) {
                        setSelectedMaterial(filteredMaterials[0].id);
                    }
                }
            } catch (error) {
                logger.error("Error fetching materials: " + error);
            }
        };

        fetchMaterials();
    }, [editMode]);

    useEffect(() => {
        if (bathtubsToConfirm != null) {
            const isAllChecked = bathtubsToConfirm.every(
                (bathtubToConfirm) => bathtubToConfirm.alertMessage === null
            );
            setAllChecked(isAllChecked);
            if (isAllChecked === false) {
                startHighlighting();
            }
        }
    }, [bathtubsToConfirm]);

    const handleNumberChange = (value) => {
        setNumberOfTrips(value);
    };

    const handleFormSubmit = async () => {
        const requestData = {
            transferGroupId: selectedTrip?.id,
            contentType: selectedMaterial,
            roundTripsNumber: numberOfTrips,
            depositId: selectedBathtubToConfirm,
            isUrgent: isUrgent,
            toCall: toCall,
        };
        if (editMode === false) {
            const response = await insertNewRequest(requestData);
            if (response.status === 200) {
                onShowSnackbar(
                    getTranslation("Nuova richiesta inserita correttamente"),
                    colors.green
                );
                handleReload();
                handleClosingModal();
            } else {
                onShowSnackbar(
                    getTranslation(response.response.data.error),
                    colors.red
                );
            }
        } else {
            const response = await editRequestData(requestData);
            if (response.status === 200) {
                onShowSnackbar(
                    getTranslation("Nuova richiesta aggiornata correttamente"),
                    colors.green
                );
                handleReload();
                handleClosingModal();
            } else {
                onShowSnackbar(
                    getTranslation(response.response.data.error),
                    colors.red
                );
            }
        }
    };

    const onShowSnackbar = (message, color) => {
        setSnackbarMessage(message);
        setSnackbarColor(color);
        setSnackbarVisible(true);
        setTimeout(() => {
            setSnackbarVisible(false);
        }, 2000);
    };

    const startHighlighting = () => {
        setInterval(() => {
            setHighlightedIndex((prevIndex) => {
                const nextIndex = bathtubsToConfirm.findIndex(
                    (bathtub, idx) =>
                        idx > (prevIndex !== null ? prevIndex : -1) &&
                        bathtub.alertMessage !== null
                );
                return nextIndex !== -1
                    ? nextIndex
                    : bathtubsToConfirm.findIndex((bathtub) => bathtub.alertMessage !== null);
            });
        }, 2000);
    };

    const handleCheckboxPress = () => {
        handleClosingModal();
        navigation.navigate(getTranslation("Gärrestmanagement"), { from: "NewRequestCard" });
    };

    const handlePickerChange = (material) => {
        setSelectedMaterial(material);
    };

    const handleBathtubChange = (bathtubToConfirm) => {
        setSelectedBathtubToConfirm(bathtubToConfirm);
    };

    if (!collapsed) {
        return null;
    }

    return (
        <View
            style={[
                styles.content,
                {
                    backgroundColor: colors.white,
                    borderRadius: 16,
                    shadowColor: colors.white,
                },
            ]}
        >
            <TouchableOpacity style={styles.closeButton} onPress={handleClosingModal}>
                <FontAwesome name="times" size={24} color={colors.red} />
            </TouchableOpacity>
            {!editMode && (
                <View style={styles.rowContainer}>
                    <View style={styles.labelContainer}>
                        <Text style={styles.label}>{getTranslation("Materiale")}</Text>
                    </View>
                    <View style={styles.inputContainer}>
                        <Picker
                            items={materials}
                            plates={false}
                            filters={true}
                            value={selectedMaterial}
                            onValueChange={(value) => handlePickerChange(value)}
                        />
                    </View>
                </View>
            )}
            {!editMode && selectedMaterial === "DigestatoLiquido" && (
                <View style={styles.rowContainer}>
                    <View style={styles.labelContainer}>
                        <Text style={styles.label}> {getTranslation("Vasca")}</Text>
                    </View>
                    <View style={styles.inputContainer}>
                        <Picker
                            field={'nameWithInfo'}
                            items={bathtubsDestination}
                            plates={false}
                            filters={true}
                            value={undefined}
                            onValueChange={(value) => handleBathtubChange(value)}
                        />
                    </View>
                </View>
            )}
            <View style={styles.rowContainer}>
                <View style={styles.labelContainer}>
                    <Text style={styles.label}>{getTranslation("Viaggi")}</Text>
                </View>
                <View style={styles.inputContainer}>
                    <NumberSelector
                        trips={true}
                        size="small"
                        onChange={handleNumberChange}
                        quantity={editMode ? selectedTrip.transferNumber : numberOfTrips}
                    />
                </View>
            </View>
            <View style={styles.rowContainer}>
                <View style={styles.labelContainer}>
                    <Text style={styles.label}>{getTranslation("Urgente")}</Text>
                </View>
                <Switch
                    value={isUrgent}
                    onValueChange={() => setIsUrgent(!isUrgent)}
                    trackColor={{ false: colors.greenWater, true: colors.green }}
                />
            </View>
            <View style={styles.rowContainer}>
                <View style={styles.labelContainer}>
                    <Text style={styles.label}>{getTranslation("Chiamami")}</Text>
                </View>
                <Switch
                    value={toCall}
                    onValueChange={() => setToCall(!toCall)}
                    trackColor={{ false: colors.greenWater, true: colors.green }}
                />
            </View>

            {bathtubsToConfirm !== null &&
                (selectedMaterial === "Liquame" ||
                    selectedMaterial === "DigestatoLiquido") && (
                    <View>
                        <View
                            style={{
                                flexDirection: "row",
                                flexWrap: "wrap",
                                justifyContent: "space-between",
                            }}
                        >
                            {bathtubsToConfirm.map((bathtubToConfirm, index) => (
                                <View
                                    key={index}
                                    style={[
                                        { width: "100%", paddingHorizontal: 5 },
                                        highlightedIndex === index &&
                                        bathtubToConfirm.alertMessage !== null &&
                                        styles.highlightedBorder,
                                    ]}
                                >
                                    <TouchableOpacity onPress={handleCheckboxPress}>
                                        <CheckBox
                                            title={bathtubToConfirm.nameWithInfo}
                                            checked={bathtubToConfirm.alertMessage === null}
                                            checkedColor={colors.green}
                                            uncheckedColor={colors.red}
                                            disabled={true}
                                        />
                                    </TouchableOpacity>
                                </View>
                            ))}
                        </View>
                    </View>
                )}
            <View style={styles.buttonContainer}>
                {selectedMaterial === "Liquame" ||
                    selectedMaterial === "DigestatoLiquido" ? (
                    <TouchableOpacity
                        disabled={!allChecked}
                        onPress={handleFormSubmit}
                        style={[
                            styles.button,
                            {
                                width: "100%",
                                flexDirection: "row",
                                alignItems: "center",
                                backgroundColor: allChecked ? colors.violet : "#808080",
                                borderRadius: 16,
                            },
                        ]}
                    >
                        <Text style={[styles.buttonText, { flex: 1, textAlign: "center" }]}>
                            {getTranslation("Inserisci")}
                        </Text>
                        <FontAwesome
                            name="pencil"
                            size={24}
                            color={colors.white}
                            style={{ marginLeft: 8 }}
                        />
                    </TouchableOpacity>
                ) : (
                    <TouchableOpacity
                        onPress={handleFormSubmit}
                        style={[
                            styles.button,
                            {
                                width: "100%",
                                flexDirection: "row",
                                alignItems: "center",
                                backgroundColor: colors.violet,
                                borderRadius: 16,
                            },
                        ]}
                    >
                        <Text style={[styles.buttonText, { flex: 1, textAlign: "center" }]}>
                            {getTranslation("Inserisci")}
                        </Text>
                        <FontAwesome
                            name="pencil"
                            size={24}
                            color={colors.white}
                            style={{ marginLeft: 8 }}
                        />
                    </TouchableOpacity>
                )}
            </View>
            {snackbarVisible && (
                <View
                    style={[
                        styles.snackbar,
                        { backgroundColor: snackbarColor, borderColor: colors.white },
                    ]}
                >
                    <Text style={styles.snackbarText}>{snackbarMessage}</Text>
                    <TouchableOpacity onPress={() => setSnackbarVisible(false)}>
                        <Text style={styles.snackbarButton}>OK</Text>
                    </TouchableOpacity>
                </View>
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    closeButton: {
        position: "absolute",
        top: 5,
        right: 10,
        zIndex: 1,
    },
    content: {
        padding: 20,
        borderRadius: 16,
        minWidth: 300,
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 5,
        elevation: 3,
    },
    rowContainer: {
        flexDirection: "row",
        alignItems: "center",
        marginBottom: 10,
    },
    labelContainer: {
        flex: 1,
    },
    inputContainer: {
        flex: 2,
    },
    buttonContainer: {
        marginTop: 20,
    },
    button: {
        padding: 10,
        borderRadius: 16,
        alignItems: "center",
    },
    buttonText: {
        color: "#fff",
        fontSize: 16,
        fontFamily: fonts.regular
    },
    highlightedBorder: {
        borderColor: "red",
        borderWidth: 2,
        borderRadius: 8,
    },
    snackbar: {
        position: "absolute",
        bottom: 20,
        left: 20,
        right: 20,
        padding: 10,
        borderWidth: 1,
        borderRadius: 8,
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
    },
    snackbarText: {
        color: colors.white,
        fontSize: 16,
        textAlign: "center",
        fontFamily: fonts.regular
    },
    snackbarButton: {
        color: colors.white,
        fontSize: 16,
        fontWeight: "bold",
    },
    label: {
        color: colors.black,
        fontSize: 16,
        fontFamily: fonts.regular
    }
});

export default NewRequestCard;
